import {
  type AdminUsersFacets,
  type AdminUsersQuery,
  DEFAULT_USER_DIR,
  DEFAULT_USER_SORT,
  DEFAULT_USERS_PAGE_SIZE,
  type LastActiveBucket,
  LAST_ACTIVE_BUCKETS,
  LAST_ACTIVE_LABELS,
  type SortDir,
  type UserAudience,
  type UserSortKey,
  type UserStatus,
  type UserStatusFilter,
  USER_SORT_KEYS,
  USER_STATUS_LABELS,
  USER_STATUS_ORDER,
  USERS_PAGE_SIZES,
  PLAN_NONE,
  TIER_NONE,
} from '@/api/admin-users';
import { PLAN_ORDER, planTitle } from '@/lib/admin/plan-facets';
import { formatDateShort } from '@/lib/dates';

/**
 * Pure filter model for /admin/users - no React, so it is unit-testable.
 *
 * The roster is fully server-driven: search, every filter, the sort and the
 * paging run in the `admin_list_users` RPC (migration 543). This module is the
 * single place the URL search params (`UsersSearch`) are parsed, normalised and
 * turned into the API query (`AdminUsersQuery`), plus the chip model the filter
 * bar renders. The URL is the source of truth; defaults are omitted from it so
 * the bare path stays clean.
 *
 * Two segmentations sit side by side and combine (R1):
 *  - `tab` is LIFECYCLE - a queue that needs a human. It writes the server's
 *    `status` param, so the tab and the long-tail Status dropdown are one
 *    control split in two and never both apply.
 *  - `audience` is the PAYMENT RELATIONSHIP - paying / granted / free.
 */

// ---- tabs --------------------------------------------------------------------

/** The five lifecycle tabs. Every non-`all` tab IS a derived-status filter. */
export type RosterTab = 'all' | 'past_due' | 'cancelling' | 'suspended' | 'not_onboarded';

export const ROSTER_TABS: RosterTab[] = [
  'all',
  'past_due',
  'cancelling',
  'suspended',
  'not_onboarded',
];

/** Statuses promoted out of the dropdown into a tab (R5 F3). */
export const TAB_STATUSES: UserStatus[] = ['past_due', 'cancelling', 'suspended', 'not_onboarded'];

/**
 * The long tail the Status dropdown keeps: someone already actioned these
 * deliberately, so they are a slice to find rather than a queue to work.
 */
export const LONG_TAIL_STATUSES: UserStatus[] = USER_STATUS_ORDER.filter(
  (s) => !TAB_STATUSES.includes(s),
);

export const ROSTER_TAB_LABELS: Record<RosterTab, string> = {
  all: 'All',
  past_due: 'Past due',
  cancelling: 'Cancelling',
  suspended: 'Suspended',
  not_onboarded: 'Not onboarded',
};

/** Count-badge tone per tab. Colour on the tab row means "this is a queue". */
export const ROSTER_TAB_TONE: Record<RosterTab, 'neutral' | 'warning' | 'danger'> = {
  all: 'neutral',
  past_due: 'warning',
  cancelling: 'warning',
  suspended: 'danger',
  not_onboarded: 'warning',
};

function isTab(v: unknown): v is RosterTab {
  return typeof v === 'string' && (ROSTER_TABS as readonly string[]).includes(v);
}

// ---- the URL contract --------------------------------------------------------

/** URL-persisted state. Every field is optional; a default is omitted. */
export type UsersSearch = {
  q?: string;
  /** Omitted for the default tab (`all`). */
  tab?: Exclude<RosterTab, 'all'>;
  /** Omitted for the default audience (`all`). */
  audience?: Exclude<UserAudience, 'all'>;
  /** The LONG-TAIL status dropdown. A tab status never lands here. */
  status?: UserStatusFilter;
  /** Tier codes, plus `none` for "no membership". */
  tiers?: string[];
  /** Alfie plan codes, plus `none` for "no plan row". K&C filters through `tiers`. */
  plans?: string[];
  /** RAW `user_community_memberships.status` values, plus `none`. */
  mstatus?: string[];
  /** Inclusive `YYYY-MM-DD` bounds on the join date. */
  joined_from?: string;
  joined_to?: string;
  last_active?: LastActiveBucket;
  /** Include the paid-but-never-signed-up pseudo-rows. */
  pending?: true;
  /**
   * "Subscription paying only" (migration 680): people whose own Stripe subscription is
   * billing in the latest revenue snapshot, as the tier or plan they hold. With it on, the
   * roster agrees with Revenue -> MRR by tier.
   */
  billing?: true;
  /** Omitted for the default sort (`joined`). */
  sort?: UserSortKey;
  /** Omitted for the default direction (`desc`). */
  dir?: SortDir;
  /** Omitted for page 1. */
  page?: number;
  /** Omitted for the default page size. */
  size?: number;
  /**
   * Legacy: the drawer's selected row (`<uuid>` or `pending:<id>`). The drawer
   * is gone - the route redirects this to the record page.
   */
  user?: string;
};

function isStatus(v: unknown): v is UserStatusFilter {
  return typeof v === 'string' && (USER_STATUS_ORDER as readonly string[]).includes(v);
}

function isSortKey(v: unknown): v is UserSortKey {
  return typeof v === 'string' && (USER_SORT_KEYS as readonly string[]).includes(v);
}

function isLastActive(v: unknown): v is LastActiveBucket {
  return typeof v === 'string' && (LAST_ACTIVE_BUCKETS as readonly string[]).includes(v);
}

/** `YYYY-MM-DD` only. The edge function drops anything else, so never send it. */
export function isYmd(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) &&
    !Number.isNaN(Date.parse(`${v}T00:00:00Z`));
}

/**
 * The derived status the URL carries. A legacy `cancelled` bookmark (the old
 * lifecycle chip) maps to `lapsed`, the server's alias for it.
 */
function normaliseStatus(v: unknown): UserStatusFilter | undefined {
  if (v === 'cancelled') return 'lapsed';
  return isStatus(v) ? v : undefined;
}

/**
 * The audience the URL carries. `all` is the default (omitted). `comp` is the
 * legacy spelling of `granted` and normalises to it; `not_paying` is a
 * DIFFERENT set (granted + free) and passes through untouched so an old
 * bookmark still resolves.
 */
function normaliseAudience(v: unknown): Exclude<UserAudience, 'all'> | undefined {
  if (v === 'comp' || v === 'granted') return 'granted';
  if (v === 'paying' || v === 'free' || v === 'not_paying') return v;
  return undefined;
}

function stringList(v: unknown): string[] {
  const raw = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const value = item.trim();
    if (value !== '' && !out.includes(value)) out.push(value);
  }
  return out;
}

/**
 * Parse raw URL search into a canonical `UsersSearch`. Unknown values are
 * dropped and every default is normalised away, so the parsed object never
 * carries a redundant param (`sort=started`, `page=1`, …).
 */
export function parseUsersSearch(search: Record<string, unknown>): UsersSearch {
  const out: UsersSearch = {};

  if (typeof search.q === 'string' && search.q.trim() !== '') out.q = search.q;

  const audience = normaliseAudience(search.audience);
  if (audience) out.audience = audience;

  // A tab and a status are the same server parameter. A status that has its own
  // tab becomes that tab (so `?status=past_due` from an old bookmark lands on
  // the Past due queue); the tab itself always wins if both are present.
  const status = normaliseStatus(search.status);
  const tab = isTab(search.tab) ? search.tab : undefined;
  const fromStatus = status && TAB_STATUSES.includes(status) ? (status as RosterTab) : undefined;
  const resolvedTab = tab ?? fromStatus;
  if (resolvedTab && resolvedTab !== 'all') out.tab = resolvedTab;
  if (status && !TAB_STATUSES.includes(status) && !out.tab) out.status = status;

  const tiers = stringList(search.tiers);
  if (tiers.length > 0) out.tiers = tiers;

  const plans = stringList(search.plans);
  if (plans.length > 0) out.plans = plans;

  const mstatus = stringList(search.mstatus);
  if (mstatus.length > 0) out.mstatus = mstatus;

  if (isYmd(search.joined_from)) out.joined_from = search.joined_from;
  if (isYmd(search.joined_to)) out.joined_to = search.joined_to;

  if (isLastActive(search.last_active)) out.last_active = search.last_active;

  if (search.pending === true || search.pending === 'true') out.pending = true;

  if (search.billing === true || search.billing === 'true') out.billing = true;

  if (isSortKey(search.sort) && search.sort !== DEFAULT_USER_SORT) out.sort = search.sort;

  if ((search.dir === 'asc' || search.dir === 'desc') && search.dir !== DEFAULT_USER_DIR) {
    out.dir = search.dir;
  }

  const page = toInt(search.page);
  if (page != null && page > 1) out.page = page;

  const size = toInt(search.size);
  if (size != null && size !== DEFAULT_USERS_PAGE_SIZE && isPageSize(size)) out.size = size;

  if (typeof search.user === 'string' && search.user !== '') out.user = search.user;

  return out;
}

function toInt(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.floor(n) : null;
}

function isPageSize(n: number): boolean {
  return (USERS_PAGE_SIZES as readonly number[]).includes(n);
}

/**
 * Normalise a (possibly patched) search into the minimal object the URL should
 * carry: empty strings, empty arrays and every default are dropped so navigate
 * writes the clean path. Used by every navigate call in the roster.
 */
export function serializeUsersSearch(s: UsersSearch): UsersSearch {
  const out: UsersSearch = {};
  if (s.q && s.q.trim() !== '') out.q = s.q;
  if (s.tab && s.tab !== ('all' as string) && isTab(s.tab)) out.tab = s.tab;
  if (s.audience) out.audience = normaliseAudience(s.audience);
  if (!out.tab && isStatus(s.status) && !TAB_STATUSES.includes(s.status)) out.status = s.status;
  if (s.tiers && s.tiers.length > 0) out.tiers = s.tiers;
  if (s.plans && s.plans.length > 0) out.plans = s.plans;
  if (s.mstatus && s.mstatus.length > 0) out.mstatus = s.mstatus;
  if (isYmd(s.joined_from)) out.joined_from = s.joined_from;
  if (isYmd(s.joined_to)) out.joined_to = s.joined_to;
  if (isLastActive(s.last_active)) out.last_active = s.last_active;
  if (s.pending) out.pending = true;
  if (s.billing) out.billing = true;
  if (s.sort && s.sort !== DEFAULT_USER_SORT) out.sort = s.sort;
  if (s.dir && s.dir !== DEFAULT_USER_DIR) out.dir = s.dir;
  if (s.page && s.page > 1) out.page = s.page;
  if (s.size && s.size !== DEFAULT_USERS_PAGE_SIZE && isPageSize(s.size)) out.size = s.size;
  if (s.user && s.user !== '') out.user = s.user;
  return out;
}

/**
 * The roster's own querystring, for the record page's `?from=` back-link.
 *
 * A list is comma-joined rather than repeated, because the back-link is parsed
 * by a plain URLSearchParams walk on the record page: a repeated key would
 * collapse to its last value there, silently dropping the rest of a
 * multi-select on the way back. `stringList` reads the csv form.
 */
export function usersSearchToQueryString(s: UsersSearch): string {
  const clean = serializeUsersSearch({ ...s, user: undefined });
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(clean)) {
    if (value == null) continue;
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  return params.toString();
}

export const activeTab = (s: UsersSearch): RosterTab => s.tab ?? 'all';

export const activePageSize = (s: UsersSearch): number => s.size ?? DEFAULT_USERS_PAGE_SIZE;

/**
 * True when no FILTER is active. Sort, page size and paging are not filters.
 * The tab and the audience ARE - Clear all resets both (R3).
 */
export function isDefaultFilters(s: UsersSearch): boolean {
  return (
    (!s.q || s.q.trim() === '') &&
    !s.tab &&
    !s.audience &&
    !s.status &&
    !s.tiers?.length &&
    !s.plans?.length &&
    !s.mstatus?.length &&
    !s.joined_from &&
    !s.joined_to &&
    !s.last_active &&
    !s.pending &&
    !s.billing
  );
}

/** Everything Clear all wipes, leaving the sort, the page size and nothing else. */
export function clearedSearch(s: UsersSearch): UsersSearch {
  return serializeUsersSearch({ sort: s.sort, dir: s.dir, size: s.size });
}

/** Map the URL search to the `admin_list_users` API query. */
export function toQuery(s: UsersSearch): AdminUsersQuery {
  // The tab and the dropdown are one server parameter; the tab wins.
  const status = s.tab ?? s.status;
  return {
    q: s.q,
    audience: s.audience,
    status,
    pending: s.pending ? true : undefined,
    billing: s.billing ? true : undefined,
    plans: s.plans && s.plans.length > 0 ? s.plans : undefined,
    tiers: s.tiers && s.tiers.length > 0 ? s.tiers : undefined,
    membership_status: s.mstatus && s.mstatus.length > 0 ? s.mstatus : undefined,
    joined_from: s.joined_from,
    joined_to: s.joined_to,
    last_active: s.last_active,
    sort: s.sort ?? DEFAULT_USER_SORT,
    dir: s.dir ?? DEFAULT_USER_DIR,
    page: s.page ?? 1,
    limit: activePageSize(s),
  };
}

// ---- facet options -----------------------------------------------------------

export interface FacetOption {
  value: string;
  label: string;
  /** `null` while the facets have not arrived - render a skeleton, never 0 (D14). */
  count: number | null;
}

/** An empty group is ABSENT from a facet, not zero (migration 543). */
function facetCount(map: Record<string, number> | undefined, key: string): number | null {
  if (!map) return null;
  return map[key] ?? 0;
}

/**
 * Tier multi-select options: every tier in the catalog (so an option never
 * vanishes when the current selection filters its count to zero), by level,
 * then "No tier" for the seatless rows.
 */
export function tierOptions(
  tiers: { code: string; name: string; tier_level: number }[],
  facets: AdminUsersFacets | undefined,
): FacetOption[] {
  const known = [...tiers].sort((a, b) => a.tier_level - b.tier_level || a.code.localeCompare(b.code));
  const options: FacetOption[] = known.map((t) => ({
    value: t.code,
    label: t.name,
    count: facetCount(facets?.tiers, t.code),
  }));
  // A code the catalog has never heard of still has to be selectable.
  for (const code of Object.keys(facets?.tiers ?? {})) {
    if (code === TIER_NONE || known.some((t) => t.code === code)) continue;
    options.push({ value: code, label: code, count: facetCount(facets?.tiers, code) });
  }
  options.push({
    value: TIER_NONE,
    label: 'No tier',
    count: facetCount(facets?.tiers, TIER_NONE),
  });
  return options;
}

/** The long-tail Status dropdown: "All statuses" plus the five it kept. */
export function statusOptions(facets: AdminUsersFacets | undefined): FacetOption[] {
  const counts = facets?.status;
  const all = counts ? Object.values(counts).reduce((sum, n) => sum + n, 0) : null;
  return [
    { value: '', label: 'All statuses', count: all },
    ...LONG_TAIL_STATUSES.map((s) => ({
      value: s,
      label: USER_STATUS_LABELS[s],
      count: facetCount(counts, s),
    })),
  ];
}

/** One label for the plan sentinel, shared by the option and its chip. */
export const PLAN_NONE_LABEL = 'No plan';

function planRank(code: string): number {
  const i = PLAN_ORDER.alfie.indexOf(code);
  return i === -1 ? PLAN_ORDER.alfie.length : i;
}

/**
 * Alfie plan options for More filters. The catalog comes from
 * `facets.plan_catalog` (so an option never vanishes at zero); counts come from
 * `facets.plans`. K&C tiers are NOT here - they filter through the Tier control
 * (R5 F4: filtering K&C through a group inside a "Plan" dropdown was the
 * terminology problem made operational).
 *
 * "No plan" closes the list, outside the catalog: it is `plan_code IS NULL`
 * (mig 545), which is NOT the `free` audience - that also holds everyone ON the
 * FREE plan. The roster already renders the two differently ("Free plan" vs
 * "-"), so neither control substitutes for the other.
 */
export function alfiePlanOptions(facets: AdminUsersFacets | undefined): FacetOption[] {
  // Nothing is known before the facets land, and an option list of one - the
  // sentinel with no catalog behind it - would be a control, not a skeleton.
  if (!facets) return [];
  const catalog = facets.plan_catalog.filter((entry) => entry.product === 'alfie');
  const options: FacetOption[] = [...catalog]
    .sort((a, b) => planRank(a.code) - planRank(b.code) || a.code.localeCompare(b.code))
    .map((entry) => ({
      value: entry.code,
      label: entry.name || planTitle(entry.code),
      count: facetCount(facets.plans, entry.code),
    }));
  options.push({
    value: PLAN_NONE,
    label: PLAN_NONE_LABEL,
    count: facetCount(facets.plans, PLAN_NONE),
  });
  return options;
}

/** Raw seat statuses present in scope, plus "No membership row". */
export function membershipStatusOptions(facets: AdminUsersFacets | undefined): FacetOption[] {
  const map = facets?.membership_status;
  const keys = Object.keys(map ?? {}).filter((k) => k !== 'none').sort();
  const options: FacetOption[] = keys.map((k) => ({
    value: k,
    label: titleFromCode(k),
    count: facetCount(map, k),
  }));
  options.push({ value: 'none', label: 'No membership row', count: facetCount(map, 'none') });
  return options;
}

export function lastActiveOptions(facets: AdminUsersFacets | undefined): FacetOption[] {
  return LAST_ACTIVE_BUCKETS.map((b) => ({
    value: b,
    label: LAST_ACTIVE_LABELS[b],
    count: facets?.last_active ? (facets.last_active[b] ?? 0) : null,
  }));
}

/** "past_due" -> "Past due". Raw seat statuses have no display table. */
export function titleFromCode(code: string): string {
  const words = code.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ---- applied-filter chips ----------------------------------------------------

/**
 * One dismissible chip per applied filter (R3). The tab and the audience get no
 * chip: both show their own state permanently on screen, and a chip exists to
 * reveal a filter you cannot otherwise see.
 */
export interface FilterChip {
  id: string;
  label: string;
  /** The patch that removes this filter. */
  clear: Partial<UsersSearch>;
}

export interface ChipLabelContext {
  tierName?: (code: string) => string;
  planName?: (code: string) => string;
}

export function activeFilterChips(s: UsersSearch, ctx: ChipLabelContext = {}): FilterChip[] {
  const chips: FilterChip[] = [];

  if (s.q && s.q.trim() !== '') {
    chips.push({ id: 'q', label: `Search: ${s.q.trim()}`, clear: { q: undefined } });
  }

  if (s.tiers?.length) {
    const names = s.tiers.map((c) =>
      c === TIER_NONE ? 'No tier' : (ctx.tierName?.(c) ?? c),
    );
    chips.push({ id: 'tiers', label: `Tier: ${names.join(', ')}`, clear: { tiers: undefined } });
  }

  if (s.status) {
    chips.push({
      id: 'status',
      label: `Status: ${USER_STATUS_LABELS[s.status]}`,
      clear: { status: undefined },
    });
  }

  if (s.plans?.length) {
    const names = s.plans.map((c) =>
      c === PLAN_NONE ? PLAN_NONE_LABEL : (ctx.planName?.(c) ?? planTitle(c)),
    );
    chips.push({
      id: 'plans',
      label: `Alfie plan: ${names.join(', ')}`,
      clear: { plans: undefined },
    });
  }

  if (s.mstatus?.length) {
    chips.push({
      id: 'mstatus',
      label: `Raw membership status: ${s.mstatus.map(titleFromCode).join(', ')}`,
      clear: { mstatus: undefined },
    });
  }

  if (s.joined_from || s.joined_to) {
    chips.push({
      id: 'joined',
      label: `Joined: ${joinedRangeLabel(s.joined_from, s.joined_to)}`,
      clear: { joined_from: undefined, joined_to: undefined },
    });
  }

  if (s.last_active) {
    chips.push({
      id: 'last_active',
      label: `Last active: ${LAST_ACTIVE_LABELS[s.last_active]}`,
      clear: { last_active: undefined },
    });
  }

  if (s.pending) {
    chips.push({
      id: 'pending',
      label: 'Pending signups only',
      clear: { pending: undefined },
    });
  }

  if (s.billing) {
    chips.push({
      id: 'billing',
      label: 'Subscription paying only',
      clear: { billing: undefined },
    });
  }

  return chips;
}

/** "from 1 Jan 2024", "to 31 Dec 2026", "1 Jan 2024 - 31 Dec 2026". */
export function joinedRangeLabel(from?: string, to?: string): string {
  const day = (ymd: string) => formatDateShort(`${ymd}T00:00:00`) || ymd;
  if (from && to) return `${day(from)} - ${day(to)}`;
  if (from) return `from ${day(from)}`;
  if (to) return `to ${day(to)}`;
  return 'any';
}
